import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Menu,
  X,
  Users,
  Calendar,
  Shield,
  Heart,
  MessageCircle,
  ArrowRight,
  CheckCircle,
  Phone,
  Mail,
  Brain,
  Video,
  Lock,
} from "lucide-react";
import EmergencyButton from "../emergency/EmergencyButton";
import EmergencyModal from "../emergency/EmergencyModal";
import QuizSelector from "../questionnaires/QuizSelector";

const Counter = ({ target }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 2000;
    const stepTime = Math.abs(Math.floor(duration / target));

    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= target) {
        clearInterval(timer);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [target]);

  return <span>{count.toLocaleString()}</span>;
};

const Home = () => {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [emergencyModalOpen, setEmergencyModalOpen] = useState(false);

  return (
    <div className="relative flex min-h-screen flex-col overflow-x-hidden bg-gradient-uzima-hero">
      {/* Modern Gradient Navigation */}
      <nav className="fixed right-0 left-0 top-0 shadow-lg bg-white/95 backdrop-blur-lg border-b border-gray-100 transition-all duration-300 z-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
          <div className="flex justify-between items-center h-20">
            {/* Logo and Title */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="h-12 w-12 rounded-xl overflow-hidden shadow-md ring-2 ring-uzima-maroon-500/20 group-hover:ring-uzima-maroon-500/40 transition-all">
                <img
                  src="/uzimalogo.jpg"
                  alt="Uzima Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.src = "/uzimalogo.jpg";
                  }}
                />
              </div>
              <div>
                <h1 className="text-xl font-display font-bold text-uzima-maroon-700">
                  Uzima
                </h1>
                <p className="text-xs text-gray-600 font-medium">Counseling & Wellbeing</p>
              </div>
            </Link>

            {/* Hamburger Button (Mobile) */}
            <div className="lg:hidden">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="text-gray-700 hover:bg-uzima-maroon-50 p-3 rounded-xl transition-all"
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center space-x-4">
              {user ? (
                <>
                  <span className="text-gray-700 font-medium px-3">
                    Welcome, {user.fullName || user.name}
                  </span>
                  <Link
                    to={`/${user.role}/dashboard`}
                    className="bg-uzima-maroon-600 text-white font-semibold px-4 py-2 rounded-xl hover:bg-uzima-maroon-700 hover:shadow-xl hover:shadow-uzima-maroon-500/20 transform hover:scale-105 transition-all duration-200"
                  >
                    Go to Dashboard
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-700 font-medium px-4 py-2 rounded-lg hover:bg-uzima-maroon-50 transition-all"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="bg-uzima-maroon-600 text-white font-semibold px-4 py-2 rounded-xl hover:bg-uzima-maroon-700 hover:shadow-xl hover:shadow-uzima-maroon-500/20 transform hover:scale-105 transition-all duration-200"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:hidden flex flex-col space-y-3 pb-6 pt-2"
            >
              {user ? (
                <>
                  <span className="text-gray-700 font-medium px-4">
                    Welcome, {user.fullName || user.name}
                  </span>
                  <Link
                    to={`/${user.role}/dashboard`}
                    className="bg-uzima-maroon-600 text-white font-semibold px-4 py-2 rounded-xl text-center hover:bg-uzima-maroon-700"
                  >
                    Go to Dashboard
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-gray-700 font-medium px-4 py-2 hover:bg-uzima-maroon-50 rounded-lg transition">
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="bg-uzima-maroon-600 text-white font-semibold px-4 py-2 rounded-xl text-center hover:bg-uzima-maroon-700"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section with Counselling Illustration */}
      <section className="relative pt-32 pb-32 px-6 overflow-hidden min-h-[85vh] flex items-center">
        {/* Counselling Illustration Background - Full Cover */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/counselling illustration.webp"
            alt="Counselling Support"
            className="w-full h-full object-cover opacity-30"
          />
          {/* Gradient Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-uzima-maroon-50/70 to-white/80"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">
          {/* Centered Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-gray-900 leading-tight mb-6">
              Your Mental Health{" "}
              <span className="bg-gradient-to-r from-uzima-maroon-600 to-uzima-white-500 bg-clip-text text-transparent">
                Matters
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-700 mb-12 leading-relaxed max-w-3xl mx-auto">
              Uzima University Counseling Center offers professional support for students and staff facing
              relationship challenges, substance abuse, family conflicts, stress, depression, anxiety, panic attacks,
              suicidal thoughts, sleep problems, and academic pressure.
            </p>

            {/* CTA Buttons - Reduced Size */}
            <div className="flex flex-wrap justify-center gap-3 mb-16">
              {!user ? (
                <>
                  <Link
                    to="/register"
                    className="bg-uzima-maroon-600 text-white font-semibold px-4 py-2 rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-sm"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => setEmergencyModalOpen(true)}
                    className="bg-red-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-red-700 hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-sm"
                  >
                    <Phone className="w-4 h-4" />
                    Emergency Help
                  </button>
                  <Link
                    to="/login"
                    className="bg-gray-100 text-gray-700 font-semibold px-4 py-2 rounded-lg border-2 border-gray-300 hover:bg-gray-200 hover:shadow-lg transition-all duration-200 text-sm"
                  >
                    Sign In
                  </Link>
                </>
              ) : (
                <Link
                  to={`/${user.role}/dashboard`}
                  className="bg-uzima-maroon-600 text-white font-semibold px-4 py-2 rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-sm"
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white/80 backdrop-blur-md border border-uzima-maroon-200 rounded-xl p-4 shadow-lg"
              >
                <p className="text-3xl font-display font-bold text-uzima-maroon-700 mb-1">
                  <Counter target={1200} />+
                </p>
                <p className="text-sm text-gray-700">Sessions Completed</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-white/80 backdrop-blur-md border border-uzima-maroon-200 rounded-xl p-4 shadow-lg"
              >
                <p className="text-3xl font-display font-bold text-uzima-maroon-700 mb-1">
                  <Counter target={500} />+
                </p>
                <p className="text-sm text-gray-700">Students & Staff Supported</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white/80 backdrop-blur-md border border-uzima-maroon-200 rounded-xl p-4 shadow-lg"
              >
                <p className="text-3xl font-display font-bold text-uzima-maroon-700 mb-1">
                  <Counter target={95} />%
                </p>
                <p className="text-sm text-gray-700">Satisfaction Rate</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
              Comprehensive Counseling Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer a range of professional mental health services tailored
              to meet diverse needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: "Individual Counseling",
                description:
                  "Confidential one-on-one sessions addressing depression, anxiety, relationship issues, substance abuse, and personal challenges",
                gradient: "from-uzima-maroon-500 to-uzima-maroon-600",
                bgColor: "blue-50",
                iconBg: "blue-100",
                iconColor: "blue-600",
              },
              {
                icon: Users,
                title: "Group Therapy",
                description:
                  "Supportive group sessions for stress management, grief counseling, academic pressure, and peer support",
                gradient: "from-uzima-maroon-500 to-uzima-maroon-600",
                bgColor: "indigo-50",
                iconBg: "indigo-100",
                iconColor: "indigo-600",
              },
              {
                icon: Shield,
                title: "Crisis Intervention",
                description:
                  "24/7 emergency support for mental health crises, suicidal thoughts, panic attacks, and immediate safety concerns",
                gradient: "from-red-500 to-red-600",
                bgColor: "red-50",
                iconBg: "red-100",
                iconColor: "red-600",
              },
              {
                icon: Video,
                title: "Virtual Counseling",
                description:
                  "Flexible online sessions for remote support and convenience",
                gradient: "from-uzima-maroon-500 to-uzima-maroon-600",
                bgColor: "purple-50",
                iconBg: "purple-100",
                iconColor: "purple-600",
              },
              {
                icon: Calendar,
                title: "Easy Scheduling",
                description:
                  "Book appointments at your convenience with your preferred counselor",
                gradient: "from-green-500 to-green-600",
                bgColor: "green-50",
                iconBg: "green-100",
                iconColor: "green-600",
              },
              {
                icon: Lock,
                title: "Confidential & Secure",
                description:
                  "Your privacy is protected with secure, encrypted sessions and records",
                gradient: "from-teal-500 to-teal-600",
                bgColor: "teal-50",
                iconBg: "teal-100",
                iconColor: "teal-600",
              },
            ].map((service, idx) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className={`bg-gradient-to-br from-white to-${service.bgColor} p-8 rounded-2xl border border-gray-200 hover:border-uzima-maroon-300 hover:shadow-xl transition-all duration-300 group`}
                >
                  <div className={`bg-${service.iconBg} w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-7 h-7 text-${service.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-display font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mental Health Assessment Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-uzima-maroon-50 via-white to-uzima-white-50">
        <div className="max-w-7xl mx-auto">
          <QuizSelector />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden"
          >
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

            <div className="relative z-10">
              <h2 className="text-4xl font-display font-bold mb-4">
                Ready to Take the First Step?
              </h2>
              <p className="text-xl text-uzima-maroon-100 mb-8 max-w-2xl mx-auto">
                Your journey to better mental health starts here. Book a session
                with our professional counselors today.
              </p>
              {!user ? (
                <div className="flex flex-wrap justify-center gap-3">
                  <Link
                    to="/register"
                    className="bg-white text-uzima-maroon-700 font-semibold px-4 py-2 rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-sm"
                  >
                    Create Account
                  </Link>
                  <button
                    onClick={() => setEmergencyModalOpen(true)}
                    className="bg-red-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-red-700 hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-sm"
                  >
                    <Phone className="w-4 h-4" />
                    Emergency Help
                  </button>
                </div>
              ) : (
                <Link
                  to={`/${user.role}/appointments/new`}
                  className="inline-block bg-white text-uzima-maroon-700 font-semibold px-4 py-2 rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-sm"
                >
                  Book an Appointment
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-uzima-maroon-800 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-xl overflow-hidden bg-white p-2">
                  <img
                    src="/uzimalogo.jpg"
                    alt="Uzima Logo"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.src = "/uzimalogo.jpg";
                    }}
                  />
                </div>
                <h3 className="text-xl font-display font-bold">
                  Uzima Counseling Center
                </h3>
              </div>
              <p className="text-uzima-maroon-100 max-w-md">
                Uzima University Counseling Center - Professional, confidential support
                addressing stress, depression, anxiety, relationship issues, substance abuse, academic
                pressure, and other mental health challenges.
              </p>
            </div>
            <div>
              <h4 className="font-display font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-uzima-maroon-100">
                {!user && (
                  <>
                    <li>
                      <Link to="/register" className="hover:text-white transition">
                        Register
                      </Link>
                    </li>
                    <li>
                      <Link to="/login" className="hover:text-white transition">
                        Login
                      </Link>
                    </li>
                  </>
                )}
                <li>
                  <a
                    href="tel:0800720648"
                    className="hover:text-white transition flex items-center gap-2"
                  >
                    <Phone className="w-4 h-4" />
                    Crisis Hotline: 0800 720 648
                  </a>
                </li>
                <li>
                  <a
                    href="tel:112"
                    className="hover:text-white transition flex items-center gap-2"
                  >
                    <Phone className="w-4 h-4" />
                    Emergency: 999 or 112
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-uzima-maroon-100">
                <p className="flex items-center gap-2 hover:text-white transition">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:counseling@uzima.ac.ke">counseling@uzima.ac.ke</a>
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Uzima University Campus
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-uzima-maroon-700 mt-8 pt-8 text-center text-uzima-maroon-100">
            <p>
              &copy; {new Date().getFullYear()} Uzima University Counseling Center. All
              rights reserved. | Confidential & Professional Mental Health Services
            </p>
          </div>
        </div>
      </footer>

      {/* Emergency Button */}
      <EmergencyButton onClick={() => setEmergencyModalOpen(true)} />

      {/* Emergency Modal */}
      <EmergencyModal
        isOpen={emergencyModalOpen}
        onClose={() => setEmergencyModalOpen(false)}
      />
    </div>
  );
};

export default Home;
